import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  MenuItem,
  Popover,
  FormGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
  Collapse,
  alpha,
  useTheme,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import {
  Description as InvoiceIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  ViewColumn as ColumnsIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as PaidIcon,
  HourglassEmpty as PendingIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import ReportHeader from '../../components/Reports/ReportHeader';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { purchaseInvoicesService } from '../../services/purchaseInvoices.service';
import { PurchaseInvoice } from '../../types/purchaseInvoices.types';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import apiClient from '../../services/api';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { fmtUnitPrice } from '../../utils/formatNumber';

const translations = {
  en: {
    title: 'Purchase Invoices Report',
    titleAr: 'تقرير فواتير الشراء',
    filters: 'Filters',
    supplier: 'Supplier',
    warehouse: 'Warehouse',
    dateFrom: 'From Date',
    dateTo: 'To Date',
    paymentStatus: 'Payment Status',
    paymentType: 'Payment Type',
    allSuppliers: 'All Suppliers',
    allWarehouses: 'All Warehouses',
    allStatuses: 'All Payment Statuses',
    allTypes: 'All Payment Types',
    refresh: 'Refresh',
    exportExcel: 'Export Excel',
    exportPdf: 'Export PDF',
    print: 'Print',
    columns: 'Columns',
    invoiceNumber: 'Invoice #',
    invoiceDate: 'Date',
    supplierName: 'Supplier',
    warehouseName: 'Warehouse',
    poNumber: 'PO #',
    paymentTypeLabel: 'Payment Type',
    subTotal: 'Subtotal',
    discount: 'Discount',
    tax: 'Tax',
    totalAmount: 'Total',
    paidAmount: 'Paid',
    remainingAmount: 'Remaining',
    paymentStatusLabel: 'Status',
    details: 'Details',
    itemCode: 'Item Code',
    itemName: 'Item',
    unitName: 'Unit',
    quantity: 'Qty',
    unitPrice: 'Unit Price',
    totalPrice: 'Total',
    loading: 'Loading...',
    noData: 'No invoices found',
    rowsPerPage: 'Rows per page:',
    totalInvoices: 'Total Invoices',
    totalAmountSummary: 'Total Amount',
    totalPaid: 'Total Paid',
    totalRemaining: 'Total Remaining',
    kwd: 'KWD',
    supplierTotal: 'Supplier Total',
    grandTotal: 'GRAND TOTAL',
    paid: 'Paid',
    partiallyPaid: 'Partially Paid',
    unpaid: 'Unpaid',
    overdue: 'Overdue',
    cash: 'Cash',
    credit: 'Credit',
    custody: 'Custody',
    bank: 'Bank',
  },
  ar: {
    title: 'Purchase Invoices Report',
    titleAr: 'تقرير فواتير الشراء',
    filters: 'الفلاتر',
    supplier: 'المورد',
    warehouse: 'المخزن',
    dateFrom: 'من تاريخ',
    dateTo: 'إلى تاريخ',
    paymentStatus: 'حالة الدفع',
    paymentType: 'نوع الدفع',
    allSuppliers: 'جميع الموردين',
    allWarehouses: 'جميع المخازن',
    allStatuses: 'جميع حالات الدفع',
    allTypes: 'جميع أنواع الدفع',
    refresh: 'تحديث',
    exportExcel: 'تصدير Excel',
    exportPdf: 'تصدير PDF',
    print: 'طباعة',
    columns: 'الأعمدة',
    invoiceNumber: 'رقم الفاتورة',
    invoiceDate: 'التاريخ',
    supplierName: 'المورد',
    warehouseName: 'المخزن',
    poNumber: 'رقم أمر الشراء',
    paymentTypeLabel: 'نوع الدفع',
    subTotal: 'المجموع الفرعي',
    discount: 'الخصم',
    tax: 'الضريبة',
    totalAmount: 'الإجمالي',
    paidAmount: 'المدفوع',
    remainingAmount: 'المتبقي',
    paymentStatusLabel: 'الحالة',
    details: 'التفاصيل',
    itemCode: 'كود الصنف',
    itemName: 'الصنف',
    unitName: 'الوحدة',
    quantity: 'الكمية',
    unitPrice: 'سعر الوحدة',
    totalPrice: 'الإجمالي',
    loading: 'جاري التحميل...',
    noData: 'لا توجد فواتير',
    rowsPerPage: 'عدد الصفوف:',
    totalInvoices: 'إجمالي الفواتير',
    totalAmountSummary: 'إجمالي المبلغ',
    totalPaid: 'إجمالي المدفوع',
    totalRemaining: 'إجمالي المتبقي',
    kwd: 'د.ك',
    supplierTotal: 'إجمالي المورد',
    grandTotal: 'الإجمالي الكلي',
    paid: 'مدفوع',
    partiallyPaid: 'مدفوع جزئياً',
    unpaid: 'غير مدفوع',
    overdue: 'متأخر',
    cash: 'نقدي',
    credit: 'آجل',
    custody: 'عهدة',
    bank: 'بنك',
  }
};

interface Supplier {
  supplierId: number;
  supplierCode: string;
  supplierName: string;
  supplierNameAr: string;
}

interface Warehouse {
  warehouseId: number;
  warehouseCode: string;
  warehouseName: string;
  warehouseNameAr: string;
}

const PurchaseInvoicesReport: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { minDate, maxDate } = useFiscalDateRange();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PurchaseInvoice[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  
  const [supplierFilter, setSupplierFilter] = useState<number | ''>('');
  const [warehouseFilter, setWarehouseFilter] = useState<number | ''>('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>('');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState<string>('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [columnsAnchor, setColumnsAnchor] = useState<null | HTMLElement>(null);

  const [visibleColumns, setVisibleColumns] = useState({
    invoiceNumber: true,
    invoiceDate: true,
    supplierName: true,
    warehouseName: true,
    poNumber: true,
    paymentType: true,
    subTotal: true,
    discount: true,
    tax: true,
    totalAmount: true,
    paidAmount: true,
    remainingAmount: true,
    paymentStatus: true,
  });

  const [stats, setStats] = useState({
    totalInvoices: 0,
    totalAmount: 0,
    totalPaid: 0,
    totalRemaining: 0,
  });

  useEffect(() => {
    loadCompanyInfo();
    loadDropdownData();
  }, []);

  useEffect(() => {
    loadData();
  }, [page, rowsPerPage, supplierFilter, warehouseFilter, dateFromFilter, dateToFilter, paymentStatusFilter, paymentTypeFilter]);

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  const loadDropdownData = async () => {
    try {
      const [suppliersResponse, warehousesResponse] = await Promise.all([
        apiClient.get<Supplier[]>('/Suppliers'),
        apiClient.get<Warehouse[]>('/Warehouses'),
      ]);
      
      setSuppliers(suppliersResponse.data);
      setWarehouses(warehousesResponse.data);
    } catch (error) {
      console.error('Failed to load dropdown data:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      const result = await purchaseInvoicesService.getAll(
        paymentStatusFilter || undefined,
        supplierFilter || undefined,
        dateFromFilter || undefined,
        dateToFilter || undefined,
        warehouseFilter || undefined
      );

      setData(result);
      setTotalRecords(result.length);

      // ✅ إصلاح: إضافة || 0 للقيم التي قد تكون undefined
      const calculatedStats = result.reduce((acc: typeof stats, inv: PurchaseInvoice) => ({
        totalInvoices: acc.totalInvoices + 1,
        totalAmount: acc.totalAmount + (inv.totalAmount || 0),
        totalPaid: acc.totalPaid + (inv.paidAmount || 0),
        totalRemaining: acc.totalRemaining + (inv.remainingAmount || 0),
      }), { totalInvoices: 0, totalAmount: 0, totalPaid: 0, totalRemaining: 0 });

      setStats(calculatedStats);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error(isRTL ? 'فشل تحميل البيانات' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleColumnToggle = (column: keyof typeof visibleColumns) => {
    setVisibleColumns(prev => ({ ...prev, [column]: !prev[column] }));
  };

  const toggleRowExpand = (invoiceId: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(invoiceId)) {
        newSet.delete(invoiceId);
      } else {
        newSet.add(invoiceId);
      }
      return newSet;
    });
  };

  const formatNumber = (num: number, decimals: number = 3): string => {
    return num.toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');
  };

  const getPaymentStatusColor = (status: string): string => {
    const lowerStatus = status?.toLowerCase() || '';
    if (lowerStatus === 'unpaid') return theme.palette.error.main;
    if (lowerStatus.includes('partial')) return theme.palette.warning.main;
    if (lowerStatus === 'paid') return theme.palette.success.main;
    if (lowerStatus.includes('overdue')) return theme.palette.error.dark;
    return theme.palette.grey[500];
  };

  const getPaymentStatusLabel = (status: string): string => {
    const lowerStatus = status?.toLowerCase() || '';
    if (lowerStatus === 'unpaid') return t.unpaid;
    if (lowerStatus.includes('partial')) return t.partiallyPaid;
    if (lowerStatus === 'paid') return t.paid;
    if (lowerStatus.includes('overdue')) return t.overdue;
    return status;
  };

  const getPaymentTypeLabel = (type: string): string => {
    const lowerType = type?.toLowerCase() || '';
    if (lowerType.includes('cash') || lowerType.includes('supplier')) return t.cash;
    if (lowerType.includes('credit')) return t.credit;
    if (lowerType.includes('custody')) return t.custody;
    if (lowerType.includes('payment') || lowerType.includes('bank')) return t.bank;
    return type;
  };

  // ✅ إصلاح: إرجاع string دائماً
  const getSupplierName = (invoice: PurchaseInvoice): string => {
    return isRTL ? (invoice.supplierNameAr || invoice.supplierName || '') : (invoice.supplierName || '');
  };

  const getWarehouseName = (invoice: PurchaseInvoice): string => {
    return isRTL ? (invoice.warehouseNameAr || invoice.warehouseName || '') : (invoice.warehouseName || '');
  };

  const groupDataBySupplier = () => {
    const grouped: { [key: string]: PurchaseInvoice[] } = {};
    data.forEach(invoice => {
      const supplier = getSupplierName(invoice);
      if (!grouped[supplier]) grouped[supplier] = [];
      grouped[supplier].push(invoice);
    });
    return grouped;
  };

  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(isRTL ? 'فواتير الشراء' : 'Purchase Invoices');

      // Add Logo
      if (companyInfo?.logoBase64) {
        try {
          const base64Data = companyInfo.logoBase64.split(',')[1];
          const imageId = workbook.addImage({
            base64: base64Data,
            extension: 'png',
          });
          worksheet.addImage(imageId, {
            tl: { col: 0, row: 0 },
            ext: { width: 100, height: 80 }
          });
        } catch (err) {
          console.warn('Could not add logo:', err);
        }
      }

      // Company Header
      worksheet.mergeCells('C1:I1');
      const companyCell = worksheet.getCell('C1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 20, bold: true, color: { argb: 'FF1565C0' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };

      worksheet.mergeCells('C2:I2');
      const addressCell = worksheet.getCell('C2');
      addressCell.value = companyInfo?.address || '';
      addressCell.font = { size: 10 };
      addressCell.alignment = { horizontal: 'center' };

      worksheet.mergeCells('C3:I3');
      const contactCell = worksheet.getCell('C3');
      contactCell.value = `${t.kwd} | ${companyInfo?.phone || ''} | ${companyInfo?.email || ''}`;
      contactCell.font = { size: 9 };
      contactCell.alignment = { horizontal: 'center' };

      worksheet.getRow(4).height = 10;

      // Report Title
      worksheet.mergeCells('A5:I5');
      const titleCell = worksheet.getCell('A5');
      titleCell.value = isRTL ? t.titleAr : t.title;
      titleCell.font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF1565C0' }
      };

      // Date Badge
      worksheet.mergeCells('J5:K5');
      const dateCell = worksheet.getCell('J5');
      dateCell.value = new Date().toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');
      dateCell.font = { size: 10, bold: true, color: { argb: 'FF1565C0' } };
      dateCell.alignment = { horizontal: 'center', vertical: 'middle' };
      dateCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE3F2FD' }
      };

      worksheet.getRow(6).height = 10;

      // Table Headers
      const headerRow = worksheet.getRow(7);
      const headers = [
        t.invoiceNumber,
        t.invoiceDate,
        t.supplierName,
        t.warehouseName,
        t.poNumber,
        t.paymentTypeLabel,
        t.subTotal,
        t.discount,
        t.tax,
        t.totalAmount,
        t.paidAmount,
        t.remainingAmount,
        t.paymentStatusLabel
      ];

      headers.forEach((header, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = header;
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF1565C0' }
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF1565C0' } },
          bottom: { style: 'thin', color: { argb: 'FF1565C0' } },
          left: { style: 'thin', color: { argb: 'FF1565C0' } },
          right: { style: 'thin', color: { argb: 'FF1565C0' } }
        };
      });

      let currentRow = 8;

      // Group by Supplier
      const grouped = groupDataBySupplier();
      Object.keys(grouped).forEach((supplierName) => {
        const groupData = grouped[supplierName];

        // Supplier Header
        worksheet.mergeCells(`A${currentRow}:M${currentRow}`);
        const supplierHeaderCell = worksheet.getCell(`A${currentRow}`);
        supplierHeaderCell.value = `📦 ${supplierName}`;
        supplierHeaderCell.font = { bold: true, size: 11, color: { argb: 'FF1565C0' } };
        supplierHeaderCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF5F5F5' }
        };
        supplierHeaderCell.alignment = { horizontal: isRTL ? 'right' : 'left', vertical: 'middle' };
        supplierHeaderCell.border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' }
        };
        currentRow++;

        let groupTotal = 0;
        let groupPaid = 0;
        let groupRemaining = 0;

        groupData.forEach((invoice, index) => {
          const row = worksheet.getRow(currentRow);
          // ✅ إصلاح: إضافة || '' و || 0 للقيم
          const rowData = [
            invoice.invoiceNumber,
            formatDate(invoice.invoiceDate),
            getSupplierName(invoice),
            getWarehouseName(invoice),
            invoice.poNumber || '',
            getPaymentTypeLabel(invoice.paymentType || ''),
            invoice.subTotal || 0,
            invoice.discountAmount || 0,
            invoice.taxAmount || 0,
            invoice.totalAmount || 0,
            invoice.paidAmount || 0,
            invoice.remainingAmount || 0,
            getPaymentStatusLabel(invoice.paymentStatus || '')
          ];

          rowData.forEach((value, colIndex) => {
            const cell = row.getCell(colIndex + 1);
            cell.value = value;
            cell.alignment = { horizontal: colIndex >= 6 && colIndex <= 11 ? 'right' : 'left', vertical: 'middle' };
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: index % 2 === 0 ? 'FFFFFFFF' : 'FFF8F9FA' }
            };
            cell.border = {
              top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
              bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
              left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
              right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
            };

            if (colIndex >= 6 && colIndex <= 11) {
              cell.numFmt = '#,##0.000';
            }
          });

          // ✅ إصلاح: إضافة || 0
          groupTotal += invoice.totalAmount || 0;
          groupPaid += invoice.paidAmount || 0;
          groupRemaining += invoice.remainingAmount || 0;
          currentRow++;
        });

        // Supplier Subtotal
        worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
        const subtotalLabelCell = worksheet.getCell(`A${currentRow}`);
        subtotalLabelCell.value = `${t.supplierTotal}: ${supplierName}`;
        subtotalLabelCell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
        subtotalLabelCell.alignment = { horizontal: 'center', vertical: 'middle' };
        subtotalLabelCell.fill = {
          type: 'gradient',
          gradient: 'angle',
          degree: 0,
          stops: [
            { position: 0, color: { argb: 'FF42A5F5' } },
            { position: 1, color: { argb: 'FF1976D2' } }
          ]
        };

        for (let col = 7; col <= 13; col++) {
          const cell = worksheet.getCell(currentRow, col);
          cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
          cell.fill = {
            type: 'gradient',
            gradient: 'angle',
            degree: 0,
            stops: [
              { position: 0, color: { argb: 'FF42A5F5' } },
              { position: 1, color: { argb: 'FF1976D2' } }
            ]
          };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
          cell.border = {
            top: { style: 'thin' },
            bottom: { style: 'thin' },
            left: { style: 'thin' },
            right: { style: 'thin' }
          };

          if (col === 10) cell.value = groupTotal;
          if (col === 11) cell.value = groupPaid;
          if (col === 12) cell.value = groupRemaining;
          
          if (col >= 10 && col <= 12) {
            cell.numFmt = '#,##0.000';
          }
        }

        currentRow++;
      });

      // Grand Total
      worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
      const grandTotalLabelCell = worksheet.getCell(`A${currentRow}`);
      grandTotalLabelCell.value = `🏆 ${t.grandTotal}`;
      grandTotalLabelCell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
      grandTotalLabelCell.alignment = { horizontal: 'center', vertical: 'middle' };
      grandTotalLabelCell.fill = {
        type: 'gradient',
        gradient: 'angle',
        degree: 0,
        stops: [
          { position: 0, color: { argb: 'FF0D47A1' } },
          { position: 1, color: { argb: 'FF1565C0' } }
        ]
      };

      for (let col = 7; col <= 13; col++) {
        const cell = worksheet.getCell(currentRow, col);
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
        cell.fill = {
          type: 'gradient',
          gradient: 'angle',
          degree: 0,
          stops: [
            { position: 0, color: { argb: 'FF0D47A1' } },
            { position: 1, color: { argb: 'FF1565C0' } }
          ]
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = {
          top: { style: 'medium' },
          bottom: { style: 'medium' },
          left: { style: 'thin' },
          right: { style: 'thin' }
        };

        if (col === 10) cell.value = stats.totalAmount;
        if (col === 11) cell.value = stats.totalPaid;
        if (col === 12) cell.value = stats.totalRemaining;
        
        if (col >= 10 && col <= 12) {
          cell.numFmt = '#,##0.000';
        }
      }

      // Column widths
      worksheet.columns = [
        { width: 18 },
        { width: 14 },
        { width: 25 },
        { width: 20 },
        { width: 15 },
        { width: 15 },
        { width: 14 },
        { width: 12 },
        { width: 12 },
        { width: 14 },
        { width: 14 },
        { width: 14 },
        { width: 16 },
      ];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `PurchaseInvoices_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);

      toast.success(isRTL ? 'تم التصدير بنجاح ✅' : 'Exported successfully ✅');
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error(isRTL ? 'فشل التصدير ❌' : 'Export failed ❌');
    }
  };

  const exportToPdf = async () => {
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;

      doc.setFillColor(245, 250, 255);
      doc.rect(0, 0, pageWidth, 45, 'F');

      if (companyInfo?.logoBase64) {
        try {
          doc.addImage(companyInfo.logoBase64, 'PNG', 12, 8, 30, 25);
        } catch (err) {
          console.warn('Could not add logo:', err);
        }
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(21, 101, 192);
      doc.text(companyInfo?.companyName || 'Company Name', pageWidth / 2, 15, { align: 'center' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(companyInfo?.address || '', pageWidth / 2, 22, { align: 'center' });
      doc.text(`${companyInfo?.phone || ''} | ${companyInfo?.email || ''}`, pageWidth / 2, 27, { align: 'center' });

      doc.setFillColor(21, 101, 192);
      doc.roundedRect(pageWidth / 2 - 60, 32, 120, 10, 3, 3, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.text('Purchase Invoices Report', pageWidth / 2, 38.5, { align: 'center' });

      doc.setFontSize(9);
      doc.setTextColor(21, 101, 192);
      doc.text(new Date().toLocaleDateString('en-GB'), pageWidth - 15, 38.5, { align: 'right' });

      const tableHeaders = [
        'Invoice #',
        'Date',
        'Supplier',
        'Warehouse',
        'PO #',
        'Type',
        'Total',
        'Paid',
        'Remaining',
        'Status'
      ];

      const tableData: any[] = [];
      const grouped = groupDataBySupplier();

      Object.keys(grouped).forEach((supplierName) => {
        const groupData = grouped[supplierName];

        tableData.push([{
          content: supplierName,
          colSpan: 10,
          styles: {
            fillColor: [25, 118, 210],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 10,
            halign: 'left'
          }
        }]);

        let groupTotal = 0;
        let groupPaid = 0;
        let groupRemaining = 0;

        groupData.forEach((invoice) => {
          // ✅ إصلاح: إضافة || 0 و || ''
          tableData.push([
            invoice.invoiceNumber,
            formatDate(invoice.invoiceDate),
            getSupplierName(invoice).substring(0, 20),
            getWarehouseName(invoice).substring(0, 15),
            invoice.poNumber || '-',
            getPaymentTypeLabel(invoice.paymentType || '').substring(0, 8),
            formatNumber(invoice.totalAmount || 0),
            formatNumber(invoice.paidAmount || 0),
            formatNumber(invoice.remainingAmount || 0),
            getPaymentStatusLabel(invoice.paymentStatus || '')
          ]);

          groupTotal += invoice.totalAmount || 0;
          groupPaid += invoice.paidAmount || 0;
          groupRemaining += invoice.remainingAmount || 0;
        });

        tableData.push([
          {
            content: `Subtotal: ${supplierName.substring(0, 20)}`,
            colSpan: 6,
            styles: {
              fillColor: [66, 165, 245],
              textColor: [255, 255, 255],
              fontStyle: 'bold',
              halign: 'center'
            }
          },
          {
            content: formatNumber(groupTotal),
            styles: { fillColor: [66, 165, 245], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' }
          },
          {
            content: formatNumber(groupPaid),
            styles: { fillColor: [66, 165, 245], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' }
          },
          {
            content: formatNumber(groupRemaining),
            styles: { fillColor: [66, 165, 245], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' }
          },
          {
            content: '',
            styles: { fillColor: [66, 165, 245] }
          }
        ]);
      });

      tableData.push([
        {
          content: 'GRAND TOTAL',
          colSpan: 6,
          styles: {
            fillColor: [13, 71, 161],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 11,
            halign: 'center'
          }
        },
        {
          content: formatNumber(stats.totalAmount),
          styles: { fillColor: [13, 71, 161], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 11, halign: 'center' }
        },
        {
          content: formatNumber(stats.totalPaid),
          styles: { fillColor: [13, 71, 161], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 11, halign: 'center' }
        },
        {
          content: formatNumber(stats.totalRemaining),
          styles: { fillColor: [13, 71, 161], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 11, halign: 'center' }
        },
        {
          content: '',
          styles: { fillColor: [13, 71, 161] }
        }
      ]);

      autoTable(doc, {
        head: [tableHeaders],
        body: tableData,
        startY: 50,
        theme: 'striped',
        headStyles: {
          fillColor: [21, 101, 192],
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: 'bold',
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [50, 50, 50]
        },
        alternateRowStyles: {
          fillColor: [248, 250, 251]
        },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 20 },
          2: { cellWidth: 35 },
          3: { cellWidth: 30 },
          4: { cellWidth: 20 },
          5: { cellWidth: 18 },
          6: { cellWidth: 22, halign: 'right' },
          7: { cellWidth: 22, halign: 'right' },
          8: { cellWidth: 22, halign: 'right' },
          9: { cellWidth: 20, halign: 'center' }
        },
        margin: { left: 10, right: 10 },
      });

      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(150, 150, 150);
        doc.setFont('helvetica', 'italic');
        doc.text(
          `Generated on ${new Date().toLocaleString('en-GB')} | Page ${i} of ${pageCount}`,
          pageWidth / 2,
          pageHeight - 8,
          { align: 'center' }
        );
      }

      doc.save(`PurchaseInvoices_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(isRTL ? 'تم التصدير بنجاح ✅' : 'Exported successfully ✅');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(isRTL ? 'فشل التصدير ❌' : 'Export failed ❌');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getPaginatedData = () => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return data.slice(startIndex, endIndex);
  };

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Print Section */}
      <Box className="print-section">
        <ReportHeader
          reportTitle={t.title}
          reportTitleAr={t.titleAr}
          reportDate={new Date().toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}
        />
        
        <TableContainer sx={{ mt: 3 }}>
          <Table size="small" sx={{ 
            fontFamily: 'Arial, "Segoe UI", Tahoma, sans-serif',
            '& th, & td': { 
              border: '1px solid #1565C0',
              p: 1.5,
              fontSize: '12px',
              fontFamily: 'Arial, "Segoe UI", Tahoma, sans-serif',
              fontWeight: 500,
              color: '#000'
            },
            '& th': {
              fontWeight: 700
            }
          }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#1565C0' }}>
                {visibleColumns.invoiceNumber && <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px', fontFamily: 'Arial, sans-serif' }}>{t.invoiceNumber}</TableCell>}
                {visibleColumns.invoiceDate && <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px', fontFamily: 'Arial, sans-serif' }}>{t.invoiceDate}</TableCell>}
                {visibleColumns.supplierName && <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px', fontFamily: 'Arial, sans-serif' }}>{t.supplierName}</TableCell>}
                {visibleColumns.warehouseName && <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px', fontFamily: 'Arial, sans-serif' }}>{t.warehouseName}</TableCell>}
                {visibleColumns.totalAmount && <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px', fontFamily: 'Arial, sans-serif' }}>{t.totalAmount}</TableCell>}
                {visibleColumns.paidAmount && <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px', fontFamily: 'Arial, sans-serif' }}>{t.paidAmount}</TableCell>}
                {visibleColumns.remainingAmount && <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px', fontFamily: 'Arial, sans-serif' }}>{t.remainingAmount}</TableCell>}
                {visibleColumns.paymentStatus && <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px', fontFamily: 'Arial, sans-serif' }}>{t.paymentStatusLabel}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((invoice, index) => (
                <TableRow key={invoice.purchaseInvoiceId} sx={{ bgcolor: index % 2 === 0 ? 'white' : '#f5f5f5' }}>
                  {visibleColumns.invoiceNumber && <TableCell>{invoice.invoiceNumber}</TableCell>}
                  {visibleColumns.invoiceDate && <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>}
                  {visibleColumns.supplierName && <TableCell>{getSupplierName(invoice)}</TableCell>}
                  {visibleColumns.warehouseName && <TableCell>{getWarehouseName(invoice)}</TableCell>}
                  {visibleColumns.totalAmount && <TableCell align="right">{formatNumber(invoice.totalAmount || 0)}</TableCell>}
                  {visibleColumns.paidAmount && <TableCell align="right">{formatNumber(invoice.paidAmount || 0)}</TableCell>}
                  {visibleColumns.remainingAmount && <TableCell align="right">{formatNumber(invoice.remainingAmount || 0)}</TableCell>}
                  {visibleColumns.paymentStatus && <TableCell>{getPaymentStatusLabel(invoice.paymentStatus || '')}</TableCell>}
                </TableRow>
              ))}
              <TableRow sx={{ bgcolor: '#0D47A1' }}>
                <TableCell colSpan={visibleColumns.warehouseName ? 4 : 3} sx={{ fontWeight: 'bold', color: 'white', fontSize: '13px' }}>{t.grandTotal}</TableCell>
                {visibleColumns.totalAmount && <TableCell align="right" sx={{ fontWeight: 'bold', color: 'white', fontSize: '13px' }}>{formatNumber(stats.totalAmount)}</TableCell>}
                {visibleColumns.paidAmount && <TableCell align="right" sx={{ fontWeight: 'bold', color: 'white', fontSize: '13px' }}>{formatNumber(stats.totalPaid)}</TableCell>}
                {visibleColumns.remainingAmount && <TableCell align="right" sx={{ fontWeight: 'bold', color: 'white', fontSize: '13px' }}>{formatNumber(stats.totalRemaining)}</TableCell>}
                {visibleColumns.paymentStatus && <TableCell></TableCell>}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Screen Section */}
      <Box className="no-print" sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), p: 1.5, borderRadius: 2, display: 'flex' }}>
            <InvoiceIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight="bold" color="primary">
              {isRTL ? t.titleAr : t.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isRTL ? 'تقرير شامل لفواتير الشراء مع التفاصيل' : 'Comprehensive purchase invoices report with details'}
            </Typography>
          </Box>
        </Box>
        <Divider />
      </Box>

      {/* Stats Cards */}
      <Box className="no-print" sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Card sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 200 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">{stats.totalInvoices}</Typography>
                <Typography variant="body2" color="text.secondary">{t.totalInvoices}</Typography>
              </Box>
              <InvoiceIcon sx={{ fontSize: 50, opacity: 0.3, color: 'primary.main' }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 200 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">{formatNumber(stats.totalAmount)}</Typography>
                <Typography variant="body2" color="text.secondary">{t.totalAmountSummary} ({t.kwd})</Typography>
              </Box>
              <MoneyIcon sx={{ fontSize: 50, opacity: 0.3, color: 'warning.main' }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 200 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">{formatNumber(stats.totalPaid)}</Typography>
                <Typography variant="body2" color="text.secondary">{t.totalPaid} ({t.kwd})</Typography>
              </Box>
              <PaidIcon sx={{ fontSize: 50, opacity: 0.3, color: 'success.main' }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 200 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">{formatNumber(stats.totalRemaining)}</Typography>
                <Typography variant="body2" color="text.secondary">{t.totalRemaining} ({t.kwd})</Typography>
              </Box>
              <PendingIcon sx={{ fontSize: 50, opacity: 0.3, color: 'error.main' }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Paper className="no-print" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Autocomplete
            options={suppliers}
            getOptionLabel={(option) => isRTL ? option.supplierNameAr : option.supplierName}
            value={suppliers.find(s => s.supplierId === supplierFilter) || null}
            onChange={(_, value) => setSupplierFilter(value?.supplierId || '')}
            renderInput={(params) => (
              <TextField {...params} label={t.supplier} size="small" />
            )}
            sx={{ minWidth: 200 }}
          />

          <Autocomplete
            options={warehouses}
            getOptionLabel={(option) => isRTL ? option.warehouseNameAr : option.warehouseName}
            value={warehouses.find(w => w.warehouseId === warehouseFilter) || null}
            onChange={(_, value) => setWarehouseFilter(value?.warehouseId || '')}
            renderInput={(params) => (
              <TextField {...params} label={t.warehouse} size="small" />
            )}
            sx={{ minWidth: 200 }}
          />

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.dateFrom}
              value={dateFromFilter ? new Date(dateFromFilter) : null}
              onChange={(date) => {
                const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setDateFromFilter(newValue);
                if (dateToFilter && newValue && dateToFilter < newValue) setDateToFilter('');
              }}
              format="dd/MM/yyyy"
              views={['year', 'month', 'day']}
              openTo="day"
              minDate={minDate}
              maxDate={dateToFilter ? new Date(dateToFilter) : maxDate}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }}
            />
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.dateTo}
              value={dateToFilter ? new Date(dateToFilter) : null}
              onChange={(date) => {
                const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setDateToFilter(newValue);
                if (dateFromFilter && newValue && dateFromFilter > newValue) setDateFromFilter('');
              }}
              format="dd/MM/yyyy"
              views={['year', 'month', 'day']}
              openTo="day"
              minDate={dateFromFilter ? new Date(dateFromFilter) : minDate}
              maxDate={maxDate}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }}
            />
          </LocalizationProvider>

          <TextField
            select
            size="small"
            label={t.paymentStatus}
            value={paymentStatusFilter}
            onChange={(e) => setPaymentStatusFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">{t.allStatuses}</MenuItem>
            <MenuItem value="Paid">{t.paid}</MenuItem>
            <MenuItem value="PartiallyPaid">{t.partiallyPaid}</MenuItem>
            <MenuItem value="Unpaid">{t.unpaid}</MenuItem>
            <MenuItem value="Overdue">{t.overdue}</MenuItem>
          </TextField>

          <TextField
            select
            size="small"
            label={t.paymentType}
            value={paymentTypeFilter}
            onChange={(e) => setPaymentTypeFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">{t.allTypes}</MenuItem>
            <MenuItem value="Cash">{t.cash}</MenuItem>
            <MenuItem value="Credit">{t.credit}</MenuItem>
            <MenuItem value="Custody">{t.custody}</MenuItem>
            <MenuItem value="PaymentAccount">{t.bank}</MenuItem>
          </TextField>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button startIcon={<RefreshIcon />} onClick={loadData} variant="outlined">
            {t.refresh}
          </Button>
          <Button startIcon={<ExcelIcon />} onClick={exportToExcel} variant="contained" color="success">
            {t.exportExcel}
          </Button>
          <Button startIcon={<PdfIcon />} onClick={exportToPdf} variant="contained" color="error">
            {t.exportPdf}
          </Button>
          <Button startIcon={<PrintIcon />} onClick={handlePrint} variant="contained">
            {t.print}
          </Button>
          <Button startIcon={<ColumnsIcon />} onClick={(e) => setColumnsAnchor(e.currentTarget)} variant="outlined">
            {t.columns}
          </Button>
        </Box>
      </Paper>

      {/* Columns Popover */}
      <Popover
        open={Boolean(columnsAnchor)}
        anchorEl={columnsAnchor}
        onClose={() => setColumnsAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {t.columns}
          </Typography>
          <FormGroup>
            {Object.keys(visibleColumns).map((col) => (
              <FormControlLabel
                key={col}
                control={
                  <Checkbox
                    checked={visibleColumns[col as keyof typeof visibleColumns]}
                    onChange={() => handleColumnToggle(col as keyof typeof visibleColumns)}
                  />
                }
                label={t[col as keyof typeof t]}
              />
            ))}
          </FormGroup>
        </Box>
      </Popover>

      {/* Main Table */}
      <TableContainer component={Paper} className="no-print" sx={{ mb: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, isDark ? 0.3 : 0.1) }}>
                <TableCell sx={{ width: 50 }}></TableCell>
                {visibleColumns.invoiceNumber && <TableCell><strong>{t.invoiceNumber}</strong></TableCell>}
                {visibleColumns.invoiceDate && <TableCell><strong>{t.invoiceDate}</strong></TableCell>}
                {visibleColumns.supplierName && <TableCell><strong>{t.supplierName}</strong></TableCell>}
                {visibleColumns.warehouseName && <TableCell><strong>{t.warehouseName}</strong></TableCell>}
                {visibleColumns.poNumber && <TableCell><strong>{t.poNumber}</strong></TableCell>}
                {visibleColumns.paymentType && <TableCell><strong>{t.paymentTypeLabel}</strong></TableCell>}
                {visibleColumns.subTotal && <TableCell align="right"><strong>{t.subTotal}</strong></TableCell>}
                {visibleColumns.discount && <TableCell align="right"><strong>{t.discount}</strong></TableCell>}
                {visibleColumns.tax && <TableCell align="right"><strong>{t.tax}</strong></TableCell>}
                {visibleColumns.totalAmount && <TableCell align="right"><strong>{t.totalAmount}</strong></TableCell>}
                {visibleColumns.paidAmount && <TableCell align="right"><strong>{t.paidAmount}</strong></TableCell>}
                {visibleColumns.remainingAmount && <TableCell align="right"><strong>{t.remainingAmount}</strong></TableCell>}
                {visibleColumns.paymentStatus && <TableCell align="center"><strong>{t.paymentStatusLabel}</strong></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {getPaginatedData().length === 0 ? (
                <TableRow>
                  <TableCell colSpan={14} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">{t.noData}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                getPaginatedData().map((invoice) => (
                  <React.Fragment key={invoice.purchaseInvoiceId}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => toggleRowExpand(invoice.purchaseInvoiceId)}
                        >
                          {expandedRows.has(invoice.purchaseInvoiceId) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      {visibleColumns.invoiceNumber && <TableCell>{invoice.invoiceNumber}</TableCell>}
                      {visibleColumns.invoiceDate && <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>}
                      {visibleColumns.supplierName && <TableCell>{getSupplierName(invoice)}</TableCell>}
                      {visibleColumns.warehouseName && <TableCell>{getWarehouseName(invoice)}</TableCell>}
                      {visibleColumns.poNumber && <TableCell>{invoice.poNumber || '-'}</TableCell>}
                      {visibleColumns.paymentType && <TableCell>{getPaymentTypeLabel(invoice.paymentType || '')}</TableCell>}
                      {visibleColumns.subTotal && <TableCell align="right">{formatNumber(invoice.subTotal || 0)}</TableCell>}
                      {visibleColumns.discount && <TableCell align="right">{formatNumber(invoice.discountAmount || 0)}</TableCell>}
                      {visibleColumns.tax && <TableCell align="right">{formatNumber(invoice.taxAmount || 0)}</TableCell>}
                      {visibleColumns.totalAmount && (
                        <TableCell align="right">
                          <Typography fontWeight="bold" color="primary">
                            {formatNumber(invoice.totalAmount || 0)}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.paidAmount && <TableCell align="right">{formatNumber(invoice.paidAmount || 0)}</TableCell>}
                      {visibleColumns.remainingAmount && <TableCell align="right">{formatNumber(invoice.remainingAmount || 0)}</TableCell>}
                      {visibleColumns.paymentStatus && (
                        <TableCell align="center">
                          <Chip
                            label={getPaymentStatusLabel(invoice.paymentStatus || '')}
                            size="small"
                            sx={{
                              bgcolor: alpha(getPaymentStatusColor(invoice.paymentStatus || ''), 0.1),
                              color: getPaymentStatusColor(invoice.paymentStatus || ''),
                              fontWeight: 'bold'
                            }}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                    
                    {/* Expandable Details Row */}
                    <TableRow>
                      <TableCell colSpan={14} sx={{ py: 0, borderBottom: expandedRows.has(invoice.purchaseInvoiceId) ? 1 : 0 }}>
                        <Collapse in={expandedRows.has(invoice.purchaseInvoiceId)} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: isDark ? alpha('#1565C0', 0.15) : '#E3F2FD' }}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="primary">
                              {t.details}
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell><strong>{t.itemCode}</strong></TableCell>
                                  <TableCell><strong>{t.itemName}</strong></TableCell>
                                  <TableCell><strong>{t.unitName}</strong></TableCell>
                                  <TableCell align="right"><strong>{t.quantity}</strong></TableCell>
                                  <TableCell align="right"><strong>{t.unitPrice}</strong></TableCell>
                                  <TableCell align="right"><strong>{t.discount}</strong></TableCell>
                                  <TableCell align="right"><strong>{t.tax}</strong></TableCell>
                                  <TableCell align="right"><strong>{t.totalPrice}</strong></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {invoice.details?.map((detail) => (
                                  <TableRow key={detail.invoiceDetailId}>
                                    <TableCell>{detail.itemCode}</TableCell>
                                    <TableCell>{isRTL ? (detail.itemNameAr || detail.itemName) : detail.itemName}</TableCell>
                                    <TableCell>{isRTL ? (detail.unitNameAr || detail.unitName) : detail.unitName}</TableCell>
                                    <TableCell align="right">{formatNumber(detail.quantity || 0)}</TableCell>
                                    <TableCell align="right">{fmtUnitPrice(detail.unitPrice || 0)}</TableCell>
                                    <TableCell align="right">{formatNumber(detail.discountAmount || 0)}</TableCell>
                                    <TableCell align="right">{formatNumber(detail.taxAmount || 0)}</TableCell>
                                    <TableCell align="right">
                                      <Typography fontWeight="bold">
                                        {formatNumber(detail.totalPrice || 0)}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        )}
        <TablePagination
          component="div"
          count={totalRecords}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={t.rowsPerPage}
        />
      </TableContainer>

      <style>
        {`
          @media print {
            @page { 
              size: A4 landscape; 
              margin: 10mm;
            }
            body * {
              visibility: hidden;
              font-family: Arial, 'Segoe UI', Tahoma, sans-serif !important;
            }
            .print-section, .print-section * {
              visibility: visible;
              font-family: Arial, 'Segoe UI', Tahoma, sans-serif !important;
            }
            .print-section {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              background: white;
            }
            .no-print { display: none !important; }
            table {
              width: 100%;
              border-collapse: collapse;
              page-break-inside: auto;
              font-family: Arial, 'Segoe UI', Tahoma, sans-serif !important;
            }
            th, td {
              font-family: Arial, 'Segoe UI', Tahoma, sans-serif !important;
              font-size: 11pt !important;
              font-weight: 500 !important;
              color: #000 !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            th {
              font-weight: 700 !important;
            }
            tr { page-break-inside: avoid; page-break-after: auto; }
            thead { display: table-header-group; }
            tfoot { display: table-footer-group; }
          }
          @media screen {
            .print-section { display: none; }
          }
        `}
      </style>
    </Box>
  );
};

export default PurchaseInvoicesReport;